import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import AuctionCountdown from "../components/common/AuctionCountdown";
import NftCard from "../components/common/NftCard";
import Panel from "../components/common/Panel";
import Seo from "../components/common/Seo";
import { t } from "../i18n/messages";
import { useAppState } from "../state/AppStateProvider";
import { buildPath } from "../utils/routing";
import {
  apiGetAuctions,
  apiGetAuctionEvents,
  apiGetMarketNfts,
  apiGetUserAuctionBids,
} from "../api/backend";
import { formatAmount, formatDateTime } from "../utils/format";
import { AUCTION_QUOTE_SYMBOL } from "../utils/contractConstants";
import {
  buildNftIdentityKey,
  buildNftIdentityKeyFromRow,
  resolveNftAddress,
  resolveNftGalleryImages,
  resolveNftImage,
  resolveTokenId,
} from "../utils/nftMedia";

const HIDDEN_HISTORY_AUCTION_IDS = new Set(["3", "4", "5"]);

export default function AuctionPage({ locale }) {
  const HISTORY_PAGE_SIZE = 15;
  const navigate = useNavigate();
  const { walletConnected, walletAddress } = useAppState();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTab, setHistoryTab] = useState("global");
  const [selectedHistoryAuctionId, setSelectedHistoryAuctionId] = useState("");
  const [globalHistoryPage, setGlobalHistoryPage] = useState(1);
  const [myBidPage, setMyBidPage] = useState(1);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const auctionsQuery = useQuery({
    queryKey: ["auction", "list", "english"],
    queryFn: () =>
      apiGetAuctions({
        status: "active",
        type: "english",
        limit: 200,
      }),
    staleTime: 15_000,
    retry: 1,
  });

  const bidsQuery = useQuery({
    queryKey: ["auction", "bids", walletAddress],
    queryFn: () => apiGetUserAuctionBids(walletAddress, { limit: 200 }),
    enabled: Boolean(
      walletConnected && walletAddress && historyOpen && historyTab === "mine",
    ),
    staleTime: 15_000,
    retry: 1,
  });

  const historyAuctionsQuery = useQuery({
    queryKey: ["auction", "history", "bids", "english"],
    queryFn: async () => {
      const settledRes = await apiGetAuctions({
        status: "settled",
        type: "english",
        limit: 200,
      });
      const settledAuctions = Array.isArray(settledRes?.auctions)
        ? settledRes.auctions
        : [];
      const auctions = settledAuctions.slice(0, 80);

      const historyRows = await Promise.all(
        auctions.map(async (auction) => {
          const auctionId = String(auction?.id || "").trim();
          if (!auctionId) return null;
          try {
            const eventsRes = await apiGetAuctionEvents(auctionId, { limit: 200 });
            const events = Array.isArray(eventsRes?.events) ? eventsRes.events : [];
            const bidEvents = events
              .filter(
                (evt) => String(evt?.event || "").trim().toLowerCase() === "bid",
              )
              .map((evt) => ({
                ...evt,
                auction_id: evt?.auction_id || auctionId,
                nft_address: evt?.nft_address || auction?.nft_address || "",
                token_id: evt?.token_id || auction?.token_id || "",
                name: auction?.name || "",
                image: auction?.image || "",
                start_price: auction?.start_price,
              }))
              .sort((a, b) => {
                const aMs = Date.parse(a?.created_at || a?.updated_at || 0);
                const bMs = Date.parse(b?.created_at || b?.updated_at || 0);
                return bMs - aMs;
              });

            return {
              ...auction,
              auction_id: auctionId,
              bid_events: bidEvents,
              latest_bid: bidEvents[0] || null,
              bid_count: bidEvents.length,
            };
          } catch {
            return {
              ...auction,
              auction_id: auctionId,
              bid_events: [],
              latest_bid: null,
              bid_count: 0,
            };
          }
        }),
      );

      return historyRows.filter(Boolean);
    },
    enabled: Boolean(historyOpen && historyTab === "global"),
    staleTime: 30_000,
    retry: 1,
  });

  const nftCatalogQuery = useQuery({
    queryKey: ["market", "nfts", "auction-page", locale],
    queryFn: async () => {
      const result = await apiGetMarketNfts(500, locale);
      return Array.isArray(result?.nfts) ? result.nfts : [];
    },
    staleTime: 60_000,
    retry: 1,
  });

  const auctions = useMemo(() => {
    return Array.isArray(auctionsQuery.data?.auctions)
      ? auctionsQuery.data.auctions
      : [];
  }, [auctionsQuery.data]);

  const bidHistory = useMemo(() => {
    return Array.isArray(bidsQuery.data?.bids) ? bidsQuery.data.bids : [];
  }, [bidsQuery.data]);

  const bidHistoryByAuction = useMemo(() => {
    const map = new Map();
    bidHistory.forEach((row) => {
      const key = String(row?.auction_id ?? "");
      if (!key) return;
      const amount = row?.amount ?? row?.bid_amount;
      if (amount == null) return;
      map.set(key, amount);
    });
    return map;
  }, [bidHistory]);

  const auctionHistoryRecords = useMemo(() => {
    const rows = Array.isArray(historyAuctionsQuery.data)
      ? historyAuctionsQuery.data
      : [];
    return [...rows].sort((a, b) => {
      const aMs = Date.parse(
        a?.end_time || a?.updated_at || a?.latest_bid?.created_at || 0,
      );
      const bMs = Date.parse(
        b?.end_time || b?.updated_at || b?.latest_bid?.created_at || 0,
      );
      return bMs - aMs;
    });
  }, [historyAuctionsQuery.data]);

  const selectedHistoryAuction = useMemo(() => {
    if (!selectedHistoryAuctionId) return null;
    return (
      auctionHistoryRecords.find(
        (row) => String(row?.auction_id || row?.id || "").trim() === selectedHistoryAuctionId,
      ) || null
    );
  }, [auctionHistoryRecords, selectedHistoryAuctionId]);

  const topLevelAuctionHistoryRecords = useMemo(
    () =>
      auctionHistoryRecords.filter(
        (row) =>
          !HIDDEN_HISTORY_AUCTION_IDS.has(
            String(row?.auction_id || row?.id || "").trim(),
          ),
      ),
    [auctionHistoryRecords],
  );

  const globalHistoryTotalPages = Math.max(
    1,
    Math.ceil(topLevelAuctionHistoryRecords.length / HISTORY_PAGE_SIZE),
  );
  const myBidTotalPages = Math.max(
    1,
    Math.ceil(bidHistory.length / HISTORY_PAGE_SIZE),
  );

  const pagedAuctionHistoryRecords = useMemo(() => {
    const start = (globalHistoryPage - 1) * HISTORY_PAGE_SIZE;
    return topLevelAuctionHistoryRecords.slice(start, start + HISTORY_PAGE_SIZE);
  }, [topLevelAuctionHistoryRecords, globalHistoryPage, HISTORY_PAGE_SIZE]);

  const pagedBidHistory = useMemo(() => {
    const start = (myBidPage - 1) * HISTORY_PAGE_SIZE;
    return bidHistory.slice(start, start + HISTORY_PAGE_SIZE);
  }, [bidHistory, myBidPage, HISTORY_PAGE_SIZE]);

  useEffect(() => {
    setGlobalHistoryPage(1);
    setMyBidPage(1);
    setSelectedHistoryAuctionId("");
  }, [historyTab]);

  useEffect(() => {
    if (globalHistoryPage > globalHistoryTotalPages) {
      setGlobalHistoryPage(globalHistoryTotalPages);
    }
  }, [globalHistoryPage, globalHistoryTotalPages]);

  useEffect(() => {
    if (myBidPage > myBidTotalPages) {
      setMyBidPage(myBidTotalPages);
    }
  }, [myBidPage, myBidTotalPages]);

  const nftInfoByKey = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(nftCatalogQuery.data) ? nftCatalogQuery.data : [];
    rows.forEach((row) => {
      const key = buildNftIdentityKeyFromRow(row);
      if (!key || map.has(key)) return;
      map.set(key, {
        image: resolveNftImage(row),
        galleryImages: resolveNftGalleryImages(row),
        name: String(row?.name || "").trim(),
        description: String(row?.description || "").trim(),
      });
    });
    return map;
  }, [nftCatalogQuery.data]);

  return (
    <>
      <Seo
        locale={locale}
        title={`Chronos Arte Node | ${t(locale, "navAuction")}`}
        description={t(locale, "auctionDesc")}
        path={buildPath(locale, "auction")}
      />

      <Panel
        className="auction-overview-panel"
        title={t(locale, "navAuction")}
        description={t(locale, "auctionPanelDesc")}
      >
        <div className="card-grid">
          <article className="card-link">
            <h3>{t(locale, "auctionEnglish")}</h3>
            <p>{t(locale, "auctionEnglishDesc")}</p>
          </article>
        </div>
      </Panel>

      <Panel
        className="auction-live-panel"
        title={t(locale, "auctionLiveTitle")}
        description={t(locale, "auctionLiveDesc")}
      >
        <div className="auction-toolbar">
          <div className="auction-filter">
            <button className="btn btn-primary" disabled>
              {t(locale, "auctionTypeEnglish")}
            </button>
          </div>
          <div className="auction-toolbar-actions">
            <button
              className="btn btn-ghost"
              onClick={() => {
                setHistoryTab("global");
                setHistoryOpen(true);
              }}
            >
              {t(locale, "auctionHistoryBtn", "历史记录")}
            </button>
          </div>
        </div>

        {auctionsQuery.isPending ? (
          <div className="status-banner loading">
            <strong>{t(locale, "auctionStatusProcessing")}</strong>
            <p>{t(locale, "auctionLoading")}</p>
          </div>
        ) : auctions.length ? (
          <div className="nft-card-grid auction-card-grid">
            {auctions.map((item) => {
              const auctionId = String(item?.id || "").trim();
              const currentBid = item?.highest_bid;
              const myBid = bidHistoryByAuction.get(auctionId);
              const tokenId = resolveTokenId(item);
              const nftAddress = resolveNftAddress(item);
              const key = buildNftIdentityKey(
                nftAddress,
                tokenId,
              );
              const nftInfo = nftInfoByKey.get(key) || {};
              const image = resolveNftImage(item) || nftInfo.image || "";
              const galleryImages = resolveNftGalleryImages(item);
              const mergedGalleryImages = galleryImages.length
                ? galleryImages
                : nftInfo.galleryImages || [];
              const artworkName = String(
                item?.name || nftInfo.name || `${t(locale, "auctionColAsset")} #${tokenId || "-"}`,
              ).trim();
              const quoteSymbol = AUCTION_QUOTE_SYMBOL;
              const auctionType = t(locale, "auctionTypeEnglish");

              return (
                <NftCard
                  key={auctionId || `auction-${tokenId}`}
                  locale={locale}
                  className="auction-nft-card"
                  image={image}
                  galleryImages={mergedGalleryImages}
                  title={artworkName}
                  tokenId={tokenId}
                  nftAddress={nftAddress}
                  resolveTokenName
                  price={formatAmount(currentBid, quoteSymbol)}
                  priceLabel={t(locale, "auctionColHighest")}
                  badge={`${auctionType} #${auctionId || "-"}`}
                  badgeTone="success"
                  subtitle={`${t(locale, "auctionColEndTime")}: ${formatDateTime(item?.end_time)}`}
                  onClick={() =>
                    navigate(buildPath(locale, `auction/${auctionId}`))
                  }
                >
                  <div className="nft-card-info-list">
                    <div className="nft-card-row">
                      <span className="nft-card-label">
                        {t(locale, "auctionCountdownTitle")}
                      </span>
                      <span className="nft-card-value">
                        <AuctionCountdown
                          endTime={item?.end_time || item?.endAt}
                          nowMs={nowMs}
                          showLabel={false}
                        />
                      </span>
                    </div>
                    <div className="nft-card-row">
                      <span className="nft-card-label">
                        {t(locale, "auctionColHighestBidder")}
                      </span>
                      <span className="nft-card-value mono">
                        {item?.highest_bidder ?? "-"}
                      </span>
                    </div>
                    <div className="nft-card-row">
                      <span className="nft-card-label">
                        {t(locale, "auctionColMyBid")}
                      </span>
                      <span className="nft-card-value">
                        {formatAmount(myBid, quoteSymbol)}
                      </span>
                    </div>
                  </div>
                </NftCard>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">{t(locale, "auctionNoAuctions")}</div>
        )}
      </Panel>

      {historyOpen ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal-card">
            <div className="modal-head">
              <h3>{t(locale, "auctionHistoryTitle", "拍卖历史")}</h3>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setHistoryOpen(false);
                  setSelectedHistoryAuctionId("");
                }}
              >
                {t(locale, "auctionHistoryClose")}
              </button>
            </div>
            <div className="auction-history-tabs">
              <button
                type="button"
                className={`btn ${historyTab === "global" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setHistoryTab("global")}
              >
                {t(locale, "auctionHistoryTabGlobal", "全体拍卖历史")}
              </button>
              <button
                type="button"
                className={`btn ${historyTab === "mine" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setHistoryTab("mine")}
              >
                {t(locale, "auctionHistoryTabMine", "我的出价")}
              </button>
            </div>
            <div className="table-wrap">
              <table>
                {historyTab === "global" ? (
                  <>
                    {selectedHistoryAuction ? (
                      <>
                        <thead>
                          <tr>
                            <th colSpan={4}>
                              <div className="auction-history-subhead">
                                <div>
                                  <strong>
                                    {t(locale, "auctionHistoryDetailTitle", "本次拍卖出价记录")}
                                  </strong>
                                  <div className="muted-text">
                                    {`${t(locale, "auctionHistoryColAuction", "拍卖 ID")}: ${selectedHistoryAuction.auction_id || "-"}`}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-ghost"
                                  onClick={() => setSelectedHistoryAuctionId("")}
                                >
                                  {t(locale, "auctionHistoryBack", "返回列表")}
                                </button>
                              </div>
                            </th>
                          </tr>
                          <tr>
                            <th>{t(locale, "auctionHistoryColTime", "时间")}</th>
                            <th>{t(locale, "auctionHistoryColBidder", "出价人")}</th>
                            <th>{t(locale, "auctionHistoryColBid", "出价")}</th>
                            <th>{t(locale, "auctionHistoryColStatus", "状态")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedHistoryAuction.bid_events?.length ? (
                            selectedHistoryAuction.bid_events.map((row, idx) => (
                              <tr key={`${row?.id || row?.tx_hash || idx}`}>
                                <td>{formatDateTime(row?.created_at || row?.updated_at)}</td>
                                <td className="mono">
                                  {row?.bidder || row?.highest_bidder || "-"}
                                </td>
                                <td>
                                  {formatAmount(
                                    row?.amount ?? row?.bid_amount,
                                    AUCTION_QUOTE_SYMBOL,
                                  )}
                                </td>
                                <td>
                                  {row?.status || t(locale, "auctionHistoryStatusCompleted", "已记录")}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4}>
                                {t(locale, "auctionHistoryEmpty", "暂无出价记录")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </>
                    ) : (
                      <>
                        <thead>
                          <tr>
                            <th>{t(locale, "auctionHistoryColAsset", "拍品")}</th>
                            <th>{t(locale, "auctionHistoryColAuction", "拍卖 ID")}</th>
                            <th>{t(locale, "auctionHistoryColStartPrice", "起拍价")}</th>
                            <th>{t(locale, "auctionHistoryColDealPrice", "成交价")}</th>
                            <th>{t(locale, "auctionHistoryColTime", "成交时间")}</th>
                            <th>{t(locale, "auctionHistoryColAction", "操作")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyAuctionsQuery.isPending ? (
                            <tr>
                              <td colSpan={5}>{t(locale, "auctionHistoryLoading", "加载中...")}</td>
                            </tr>
                          ) : topLevelAuctionHistoryRecords.length ? (
                            pagedAuctionHistoryRecords.map((row, idx) => {
                              const tokenId = resolveTokenId(row);
                              const nftAddress = resolveNftAddress(row);
                              const nftKey = buildNftIdentityKey(nftAddress, tokenId);
                              const nftInfo = nftInfoByKey.get(nftKey) || {};
                              const image = resolveNftImage(row) || nftInfo.image || "";
                              const name = String(
                                row?.name ||
                                  nftInfo.name ||
                                  `${t(locale, "auctionColAsset", "Asset")} #${tokenId || "-"}`,
                              ).trim();
                              const finalBidAmount =
                                row?.highest_bid ??
                                row?.latest_bid?.amount ??
                                row?.latest_bid?.bid_amount;
                              return (
                                <tr key={`${row?.auction_id || row?.id || idx}`}>
                                  <td>
                                    <div className="auction-history-asset">
                                      {image ? (
                                        <img
                                          src={image}
                                          alt={name}
                                          className="auction-history-thumb"
                                        />
                                      ) : (
                                        <div className="auction-history-thumb placeholder" />
                                      )}
                                      <div>
                                        <div>{name}</div>
                                        <div className="muted-text">{`#${tokenId || "-"}`}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="mono">{row?.auction_id || "-"}</td>
                                  <td>{formatAmount(row?.start_price, AUCTION_QUOTE_SYMBOL)}</td>
                                  <td>{formatAmount(finalBidAmount, AUCTION_QUOTE_SYMBOL)}</td>
                                  <td>{formatDateTime(row?.end_time || row?.updated_at)}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-ghost"
                                      onClick={() =>
                                        setSelectedHistoryAuctionId(
                                          String(row?.auction_id || row?.id || "").trim(),
                                        )
                                      }
                                    >
                                      {t(locale, "auctionHistoryViewDetail", "查看详情")}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={5}>{t(locale, "auctionHistoryEmpty", "暂无出价记录")}</td>
                            </tr>
                          )}
                        </tbody>
                        {topLevelAuctionHistoryRecords.length ? (
                          <tfoot>
                            <tr>
                              <td colSpan={5}>
                                <div className="auction-bid-pagination">
                                  <button
                                    type="button"
                                    className="btn btn-ghost"
                                    disabled={globalHistoryPage <= 1}
                                    onClick={() =>
                                      setGlobalHistoryPage((prev) => Math.max(1, prev - 1))
                                    }
                                  >
                                    {t(locale, "auctionBidPrevPage", "上一页")}
                                  </button>
                                  <span className="auction-bid-page-info">
                                    {`${globalHistoryPage} / ${globalHistoryTotalPages}`}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-ghost"
                                    disabled={globalHistoryPage >= globalHistoryTotalPages}
                                    onClick={() =>
                                      setGlobalHistoryPage((prev) =>
                                        Math.min(globalHistoryTotalPages, prev + 1),
                                      )
                                    }
                                  >
                                    {t(locale, "auctionBidNextPage", "下一页")}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          </tfoot>
                        ) : null}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <thead>
                      <tr>
                        <th>{t(locale, "auctionHistoryColTime", "时间")}</th>
                        <th>{t(locale, "auctionHistoryColAuction", "拍卖 ID")}</th>
                        <th>{t(locale, "auctionHistoryColBid", "出价")}</th>
                        <th>{t(locale, "auctionHistoryColStatus", "状态")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!walletConnected || !walletAddress ? (
                        <tr>
                          <td colSpan={4}>{t(locale, "auctionMsgConnectWallet", "请先连接钱包")}</td>
                        </tr>
                      ) : bidsQuery.isPending ? (
                        <tr>
                          <td colSpan={4}>{t(locale, "auctionHistoryLoading", "加载中...")}</td>
                        </tr>
                      ) : bidHistory.length ? (
                        pagedBidHistory.map((row, idx) => (
                          <tr key={`${row?.id || row?.tx_hash || idx}`}>
                            <td>{formatDateTime(row?.created_at)}</td>
                            <td>{row?.auction_id || "-"}</td>
                            <td>
                              {formatAmount(
                                row?.amount ?? row?.bid_amount,
                                AUCTION_QUOTE_SYMBOL,
                              )}
                            </td>
                            <td>
                              {row?.status || t(locale, "auctionHistoryStatusPending", "处理中")}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4}>{t(locale, "auctionHistoryEmpty", "暂无出价记录")}</td>
                        </tr>
                      )}
                    </tbody>
                    {bidHistory.length ? (
                      <tfoot>
                        <tr>
                          <td colSpan={4}>
                            <div className="auction-bid-pagination">
                              <button
                                type="button"
                                className="btn btn-ghost"
                                disabled={myBidPage <= 1}
                                onClick={() => setMyBidPage((prev) => Math.max(1, prev - 1))}
                              >
                                {t(locale, "auctionBidPrevPage", "上一页")}
                              </button>
                              <span className="auction-bid-page-info">
                                {`${myBidPage} / ${myBidTotalPages}`}
                              </span>
                              <button
                                type="button"
                                className="btn btn-ghost"
                                disabled={myBidPage >= myBidTotalPages}
                                onClick={() =>
                                  setMyBidPage((prev) => Math.min(myBidTotalPages, prev + 1))
                                }
                              >
                                {t(locale, "auctionBidNextPage", "下一页")}
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </>
                )}
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
